import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import Checkbox from './Checkbox';
import useHiddenBodyOverflow from '../utils/useHiddenBodyOverflow';
import {api} from '../utils/api';
import auth from '../utils/auth';

/**
 * 앱 설치 안내 공지 팝업.
 * '다시 보지 않기'는 이 기기(localStorage)와, 로그인한 회원이면 서버에도 남겨 다른 기기에서도 다시 뜨지 않게 한다.
 * 새 공지를 띄우려면 NOTICE_KEY 를 바꾼다.
 */
const NOTICE_KEY = 'app-download-2026-10';
const STORAGE_KEY = `noticeDismissed:${NOTICE_KEY}`;
const APK_URL = 'https://chordscore.com/app/chordscore.apk';

// 공지를 켤 때 REACT_APP_APP_NOTICE=true 로 빌드한다 (APK 정상 작동 확인 후 공개)
const ENABLED = process.env.REACT_APP_APP_NOTICE === 'true';

const ua = navigator.userAgent;
const inApp = /chordscore\/(Android|iOS)/.test(ua);
const isIOS = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);

const readLocal = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
        return false;
    }
};

const AppNotice: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [dontShow, setDontShow] = useState(false);
    useHiddenBodyOverflow(open);

    useEffect(() => {
        if (!ENABLED || inApp || readLocal()) return;
        let canceled = false;
        const check = async () => {
            if (auth.getToken()) {
                try {
                    const {data} = await api.get('/notice/dismissed');
                    if (data.success && data.data.includes(NOTICE_KEY)) {
                        try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
                        return;
                    }
                } catch (e) {
                    // 서버 확인이 안 되면 그냥 보여준다
                }
            }
            if (!canceled) setOpen(true);
        };
        const t = setTimeout(check, 800);
        return () => {
            canceled = true;
            clearTimeout(t);
        };
    }, []);

    const close = () => {
        if (dontShow) {
            try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
            if (auth.getToken()) api.post('/notice/dismiss', {key: NOTICE_KEY}).catch(() => null);
        }
        setOpen(false);
    };

    if (!open) return null;

    const android = (
        <Section key="android">
            <SectionTitle>안드로이드폰</SectionTitle>
            <Download href={APK_URL} download>앱 내려받기</Download>
            <Steps>
                <li>위 버튼을 눌러 파일을 내려받아요.</li>
                <li>내려받은 파일을 눌러 열어요.</li>
                <li>‘출처를 알 수 없는 앱’ 안내가 나오면 <b>허용</b>을 눌러요.</li>
                <li>
                    ‘안전하지 않은 앱’(Play 프로텍트) 경고가 나오면 바로 닫지 말고 <b>세부정보</b>를 눌러 아래로 펼쳐요.
                    숨어 있던 <b>무시하고 설치</b>(그래도 설치)를 눌러요.
                </li>
            </Steps>
            <Note>예전 코드스코어 앱이 있어서 설치가 안 되면, 예전 앱을 지우고 다시 설치해 주세요. 회원 정보는 그대로예요.</Note>
        </Section>
    );

    const iphone = (
        <Section key="iphone">
            <SectionTitle>아이폰</SectionTitle>
            <Steps>
                <li><b>사파리</b>로 chordscore.com 에 들어와요.</li>
                <li>화면 아래 <b>공유 버튼</b>(네모에 위쪽 화살표)을 눌러요.</li>
                <li><b>홈 화면에 추가</b>를 누르고 오른쪽 위 <b>추가</b>를 눌러요.</li>
            </Steps>
            <Note>홈 화면에 생긴 코드스코어 아이콘으로 앱처럼 쓸 수 있어요.</Note>
        </Section>
    );

    return ReactDOM.createPortal(
        <Backdrop onClick={close}>
            <Dialog role="dialog" aria-modal="true" aria-labelledby="app-notice-title" onClick={(e) => e.stopPropagation()}>
                <Body>
                    <Title id="app-notice-title">코드스코어를 앱으로 쓰세요</Title>
                    <Lead>많이 요청해 주신 앱이 준비됐어요. 스토어 등록 전까지는 아래 방법으로 설치해 주세요.</Lead>
                    {isIOS ? [iphone, android] : [android, iphone]}
                </Body>
                <Footer>
                    <Checkbox name="dontShowAppNotice" checked={dontShow} onChange={() => setDontShow(!dontShow)}>
                        다시 보지 않기
                    </Checkbox>
                    <CloseButton type="button" onClick={close}>닫기</CloseButton>
                </Footer>
            </Dialog>
        </Backdrop>,
        document.getElementById('root') as HTMLElement,
    );
};

export default AppNotice;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(4, 4, 4, 0.7);
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  max-height: 100%;
  border-radius: 15px;
  overflow: hidden;
  background: white;
  color: #1e1e1e;
  word-break: keep-all;
  html[color-mode='dark'] & {
    background: #212632;
    color: #f2f2f2;
  }
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 24px 22px 8px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
`;

const Lead = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  opacity: 0.8;
`;

const Section = styled.section`
  padding: 14px 0;
  border-top: 1px solid rgba(128, 128, 128, 0.25);
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: bold;
`;

const Download = styled.a`
  display: block;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #0a62ba;
  color: white !important;
  font-size: 15px;
  font-weight: bold;
  text-align: center;
  text-decoration: none;
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.7;
  list-style: decimal;
  & b {
    font-weight: bold;
  }
`;

const Note = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  opacity: 0.65;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  border-top: 1px solid rgba(128, 128, 128, 0.25);
  font-size: 14px;
`;

const CloseButton = styled.button`
  padding: 8px 16px;
  border: 0;
  border-radius: 6px;
  background: rgba(128, 128, 128, 0.18);
  color: inherit;
  font-size: 14px;
  cursor: pointer;
`;
